import React, { SyntheticEvent, useState } from 'react';
import { Button, Image, LinkIcon, Text } from '@asigna/kit';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { acceptInviteMultisigRequest } from 'api';
import cx from 'classnames';
import { NotificationCard } from 'components';
import { CopyAddress } from 'components/CopyAddress/CopyAddress';
import { OwnerAvatar } from 'components/OwnerAvatar';
import { NETWORK } from 'constants/base';
import DeleteIcon from 'images/delete_icon.svg';
import EditIcon from 'images/edit.svg';
import AddUserIcon from 'images/icon-adduser.svg';
import DotsIcon from 'images/icon-dots.svg';
import LoadGif from 'images/loading-gif.gif';
import { uniqueId } from 'lodash';
import { toast } from 'sonner';
import { StxChain } from 'store/types';
import { IContact } from 'types/contacts';
import { StxChainEnum } from 'types/enums';
import { getAccountExplorerUrl, shortenAddress } from 'utils/helper';

import { IMultiSignWallet } from '../../types';

import { useOwnerCardStyles } from './style';

interface OwnerCardProps {
  name: string;
  address: string;
  network: NETWORK;
  safeId: string;
  addressWallet: string;
  onAddContact?: (address: string) => void;
  verified?: boolean;
  canJoin?: boolean;
  onInviteAccepted?: (createdWallet?: IMultiSignWallet) => Promise<void>;
  onEdit?: (contact: IContact) => void;
  onDelete?: (id: string) => void;
  onSend?: (address: string) => void;
  contact?: IContact;
  index?: number;
}

export const OwnerCard: React.FC<OwnerCardProps> = ({
  name,
  address,
  network,
  safeId,
  addressWallet,
  onAddContact,
  verified,
  canJoin,
  onInviteAccepted,
  onEdit,
  onDelete,
  contact,
  index = -1,
  onSend,
}) => {
  const classes = useOwnerCardStyles();
  const [hoveredCard, setHoverCard] = useState(false);
  const [joining, setJoining] = useState('');
  const [openActions, setOpenActions] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const handleJoinClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    if (!canJoin) return;
    setJoining(address);
    acceptInvite(safeId).then((res) => {
      if (onInviteAccepted) {
        onInviteAccepted(res).then(() => setJoining(''));
      } else {
        setJoining('');
      }
    });
  };

  const acceptInvite = async (inviteId: string): Promise<IMultiSignWallet | undefined> =>
    acceptInviteMultisigRequest(inviteId, StxChainEnum[network] as unknown as StxChain)
      .then((res) => {
        if (!res.data) return;

        toast(
          () => (
            <NotificationCard
              title="Signer joined"
              message={`Signer ${shortenAddress(address || '')} joined successfully.`}
              type="success"
            />
          ),
          {
            className: 'w-full',
          }
        );

        return res.data.wallet;
      })
      .catch((error) => {
        console.error('An error occurred while accepting safe invite request:', error);

        return undefined;
      });

  return (
    <div
      className="flex flex-col p-12 sm:p-16 md:p-20 rounded-[12px] bg-black-2 border border-black-4 border-[.5px] min-h-[120px] hover:border-primary hover:bg-primary-5 cursor-pointer"
      onMouseEnter={() => setHoverCard(true)}
      onMouseLeave={() => setHoverCard(false)}
      key={uniqueId(address)}
      onClick={() => {
        setOpenActions(false);
        onEdit && contact && onEdit(contact);
      }}
    >
      <div className="flex gap-12 justify-start items-start self-stretch">
        <OwnerAvatar address={address} size="40px" index={index} />
        <div className="flex flex-col items-start gap-4 flex-1">
          <div className="flex items-start gap-8 sm:gap-4">
            <Text message={name} size="m15" color="white" />
            {onAddContact && name === 'Unknown' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setHoverCard(false);
                  onAddContact(address);
                }}
                className={cx(
                  'text-r12 text-primary items-center',
                  hoveredCard || isMobile ? 'flex' : 'hidden'
                )}
              >
                <img src={AddUserIcon} alt="" className="mr-[3px]" /> Add
              </button>
            )}
          </div>

          <div className="flex flex-1 items-center gap-8">
            <Text message={shortenAddress(address)} size="r14" color="regular" />
            <CopyAddress address={address} />
            <LinkIcon href={getAccountExplorerUrl(address, network)} />
          </div>
          {addressWallet === address && !verified && joining !== address && (
            <Button
              disabled={!canJoin}
              onClick={handleJoinClick}
              message="Join"
              colorType="primary"
              size="sm"
              fullWidth
              className="mt-8"
            />
          )}
          {onSend && (
            <Button
              colorType="secondary"
              fullWidth
              onClick={() => onSend(address)}
              message="Send"
              size="sm"
            />
          )}
        </div>
        {addressWallet.length > 0 &&
          ((verified && joining !== address) ||
            joining === address ||
            (!verified && addressWallet !== address && joining !== address)) && (
            <div className="flex">
              {joining === address && (
                <div className="flex gap-4 items-center">
                  <Image size={12} src={LoadGif} />
                  <Text message="Joining" size="r13" color="regular" />
                </div>
              )}
              {!verified && addressWallet !== address && joining !== address && (
                <div className="flex gap-4 items-center">
                  <div className="w-4 h-4 rounded-[50%] bg-regular" />
                  <Text message="Pending" size="r13" color="regular" />
                </div>
              )}
              {verified && joining !== address && (
                <div className="flex gap-4 items-center">
                  <div className="w-4 h-4 rounded-[50%] bg-success" />
                  <Text message="Joined" size="r13" color="success" />
                </div>
              )}
            </div>
          )}
        {onEdit && onDelete && (
          <div className={classes.actions}>
            {isMobile ? (
              <div className={classes.delete}>
                <img
                  src={DeleteIcon}
                  alt=""
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenActions(false);
                    onDelete && contact?._id && onDelete(contact._id);
                  }}
                />
              </div>
            ) : (
              <div
                className={classes.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenActions(true);
                }}
              >
                <img src={DotsIcon} alt="" />
              </div>
            )}
            {openActions && (
              <div
                className="fixed inset-0 z-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenActions(false);
                }}
              />
            )}
            {openActions && (
              <div
                className={cx(
                  'mt-4 w-[137px] absolute right-0 rounded-[6px] flex flex-col items-center z-10 py-4 bg-black-3 border-[.5px] border-white-10',
                  openActions ? 'h-auto' : 'h-0'
                )}
              >
                <div
                  className="flex items-center justify-between w-full p-12 hover:bg-white-10 transition-all"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenActions(false);
                    onEdit && contact && onEdit(contact);
                  }}
                >
                  <div className="flex items-center justify-start">
                    <img src={EditIcon} alt="" />
                    <span className="ml-8 text-white text-m14">Edit</span>
                  </div>
                </div>
                <div
                  className="flex items-center justify-between w-full p-12 hover:bg-white-10 transition-all"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenActions(false);
                    onDelete && contact?._id && onDelete(contact._id);
                  }}
                >
                  <div className="flex items-center justify-start">
                    <img src={DeleteIcon} alt="" />
                    <span className="ml-8 text-white text-m14">Delete</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
